/**
 * hook for global metadoc keyboard shortcuts like escape and page navigation
 * coordinates between page navigation, selection clearing, and toolbar actions
 */
import { useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { usePageNavigation } from "./usePageNavigation";

import type { MetaDocToolbarHandle } from "../../metadoc.types";
import type { UntaggedPageDirection } from "../../utils/untaggedPageUtils";

type MetaDocToolbarHotkeyHandle = MetaDocToolbarHandle & {
  zoomIn?: () => void;
  zoomOut?: () => void;
};

const ZOOM_HOTKEY_THROTTLE_MS = 140;

export const useMetadocKeyboardShortcuts = (
  currentPage: number,
  pageCount: number,
  onPageChange: (page: number) => void,
  selectedPagesSize: number,
  onClearSelection: () => void,
  toolbarRef: React.RefObject<MetaDocToolbarHandle>,
  onGoToUntaggedPage?: (direction: UntaggedPageDirection) => void,
) => {
  const lastZoomHotkeyAtRef = useRef(0);
  const navigation = usePageNavigation({
    currentPage,
    totalPages: pageCount || 1,
    onPageChange,
  });

  // arrow key navigation: up/down for single page
  useHotkeys(
    "down",
    () => navigation.goToNextPage(),
    { preventDefault: true },
    [navigation.goToNextPage],
  );

  useHotkeys(
    "up",
    () => navigation.goToPreviousPage(),
    { preventDefault: true },
    [navigation.goToPreviousPage],
  );

  // shift + arrow keys: jump 10 pages
  useHotkeys(
    "shift+down",
    () => navigation.goForward10Pages(),
    { preventDefault: true },
    [navigation.goForward10Pages],
  );

  useHotkeys(
    "shift+up",
    () => navigation.goBack10Pages(),
    { preventDefault: true },
    [navigation.goBack10Pages],
  );

  // left/right arrows: first/last page
  // disabled when there's an active selection
  useHotkeys(
    "left",
    () => {
      // disable when there's an active selection
      if (selectedPagesSize > 0) {
        return;
      }
      navigation.goToFirstPage();
    },
    { preventDefault: true },
    [selectedPagesSize, navigation.goToFirstPage],
  );

  useHotkeys(
    "right",
    () => {
      // disable when there's an active selection
      if (selectedPagesSize > 0) {
        return;
      }
      navigation.goToLastPage();
    },
    { preventDefault: true },
    [selectedPagesSize, navigation.goToLastPage],
  );

  useHotkeys(
    "ctrl+bracketleft",
    () => onGoToUntaggedPage?.("previous"),
    { enabled: Boolean(onGoToUntaggedPage), preventDefault: true },
    [onGoToUntaggedPage],
  );

  useHotkeys(
    "ctrl+bracketright",
    () => onGoToUntaggedPage?.("next"),
    { enabled: Boolean(onGoToUntaggedPage), preventDefault: true },
    [onGoToUntaggedPage],
  );

  // ctrl +/-: zoom in/out
  useHotkeys(
    ["ctrl+equal", "ctrl+shift+equal"],
    (e) => {
      e.preventDefault();

      const now = Date.now();
      const timeSinceLastHotkey = now - lastZoomHotkeyAtRef.current;
      if (timeSinceLastHotkey < ZOOM_HOTKEY_THROTTLE_MS) {
        return;
      }

      lastZoomHotkeyAtRef.current = now;
      const toolbar = toolbarRef.current as MetaDocToolbarHotkeyHandle | null;
      toolbar?.zoomIn?.();
    },
    { enableOnFormTags: true, preventDefault: true },
  );

  useHotkeys(
    ["ctrl+minus", "ctrl+shift+minus"],
    (e) => {
      e.preventDefault();

      const now = Date.now();
      const timeSinceLastHotkey = now - lastZoomHotkeyAtRef.current;
      if (timeSinceLastHotkey < ZOOM_HOTKEY_THROTTLE_MS) {
        return;
      }

      lastZoomHotkeyAtRef.current = now;
      const toolbar = toolbarRef.current as MetaDocToolbarHotkeyHandle | null;
      toolbar?.zoomOut?.();
    },
    { enableOnFormTags: true, preventDefault: true },
  );

  // commander: cmd+k / ctrl+k to focus page input
  useHotkeys(
    ["meta+k", "ctrl+k"],
    (e) => {
      e.preventDefault();
      toolbarRef.current?.focusPageInput();
    },
    { enableOnFormTags: true }, // allow triggering even if another input is focused
  );

  // escape to clear selection
  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      if (selectedPagesSize > 0) {
        onClearSelection();
      }
    },
    { enableOnFormTags: false },
    [selectedPagesSize],
  );
};
